import { enterpriseService } from "../../../../../services/enterprise.service";
import { groupService } from "../../../../../services/group.service";
import { fleetService } from "../../../../../services/fleet.service";
import { sectorService } from "../../../../../services/sector.service";
import { activityService } from "../../../../../services/activity.service";
import { thingService } from "../../../../../services/thing.service";
import { typeService } from "../../../../../services/type.service";
import { countryService } from "../../../../../services/country.service";
import { DialogGroup } from "../../../../../tools/dialog";
import { DialogFleet } from "../../../../../tools/dialog";
import { EventHub, THING_COUNTRIES } from "../../../../../constants/constants";
import { mapActions } from "vuex";
import { clone } from "ramda";
import AutocompleteComponent from "@/components/commons/autocomplete/AutocompleteComponent.vue";

export default {
  name: "ThingTypeTabComponent",
  components: {
    AutocompleteComponent,
  },
  model: {
    prop: "model",
    event: "change",
  },
  props: {
    model: {
      type: Object,
      required: true,
    },
    validationRules: {
      type: Object,
      required: true,
    },
    validForm: {
      type: Boolean,
      required: true,
    },
    resetFormValidation: {
      type: Boolean,
      required: true,
    },
  },
  data: () => ({
    // empresas
    enterprises: [],
    // tipos
    types: [],
    // grupos
    groups: [],
    // sectores
    sectors: [],
    // actividades
    activities: [],
    // flotas
    fleets: [],
    // países
    countries: [],
    // si el usuario eligió el país a mano, no se reemplaza por el de la empresa al cambiar de empresa
    countryManuallySelected: false,
    // promesa de la carga de países (la precarga del país de la empresa necesita la lista)
    countriesLoaded: null,
    // loading
    loadingEnterprises: false,
    loadingGroups: false,
    loadingSectors: false,
    loadingActivities: false,
    loadingFleets: false,
    loadingTypes: false,
    loadingCountries: false,
    // model para el formulario
    isValidForm: false,
    hasTracksComputed: false,
  }),
  computed: {},
  created() {
    // Evento lanzado por el diálogo de creación de grupo, cuando se guarda el nuevo grupo
    this.$eventBus.$on(EventHub.COMMIT_GROUP, (groupDto) => {
      this.getGroups(this.model.enterpriseId, groupDto._id);
    });

    // Evento lanzado por el diálogo de creación de flota, cuando se guarda la nueva flota
    this.$eventBus.$on(EventHub.COMMIT_FLEET, (fleetDto) => {
      this.getFleets(this.model.enterpriseId, fleetDto._id);
    });
    this.initSelectorsData();
  },
  beforeDestroy() {
    // desuscripción al bus de eventos
    this.$eventBus.$off(EventHub.COMMIT_GROUP);
    this.$eventBus.$off(EventHub.COMMIT_FLEET);
  },
  async mounted() {
    this.getEnterprises();
    this.countriesLoaded = this.getCountries();
    if (this.model.update) {
      this.hasTracksComputed = await thingService.hasTracksComputed(
        this.model.thingId
      );
    }
  },
  methods: {
    /**
     * actions del breadcrumb
     */
    ...mapActions("breadcrumbs", ["pushBread"]),
    /**
     * Para inicializar los selectores en la actualización de un equipo
     */
    async initSelectorsData() {
      await this.getTypes();
      if (this.model.update || this.model.isClone) {
        this.setEnterprise(this.model.enterpriseId, true);
        this.setThingType(this.model.typeId, true);
        this.setSector(this.model.sectorId, true);
        // en la edición no se precarga el país: si el equipo no tiene, el usuario debe elegirlo
        this.countryManuallySelected = true;
      }
    },
    /**
     * Carga en el selector los países habilitados para equipos (THING_COUNTRIES),
     * con el _id del catálogo y el nombre en español
     */
    async getCountries() {
      this.loadingCountries = true;
      const catalog = (await countryService.getCountries()) || [];
      const normalize = (name) => (name || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").trim().toLowerCase();
      this.countries = THING_COUNTRIES.map((thingCountry) => {
        const country = catalog.find((c) => thingCountry.aliases.includes(normalize(c.name)));
        return country ? { _id: country._id, name: thingCountry.name } : null;
      }).filter((country) => country);
      this.loadingCountries = false;
    },
    /**
     * Asigna al equipo el país de la empresa (si es un país habilitado), salvo que el usuario ya haya elegido uno
     * @param {*} enterpriseId
     */
    async setCountryFromEnterprise(enterpriseId) {
      if (!enterpriseId || this.countryManuallySelected) {
        return;
      }
      const enterprise = await enterpriseService.findOne(enterpriseId);
      await this.countriesLoaded;
      const isAllowedCountry = this.countries.some((country) => country._id === enterprise?.countryId);
      // se vuelve a validar porque mientras se buscaba la empresa pudo cambiar la selección
      if (!this.countryManuallySelected && this.model.enterpriseId === enterpriseId && isAllowedCountry) {
        this.model.countryId = enterprise.countryId;
      }
    },
    /**
     * Carga las empresas en el selector
     */
    async getEnterprises() {
      this.loadingEnterprises = true;
      const enterprises = await enterpriseService.getAllForSelector();
      this.enterprises = enterprises?.sort(function sort(a, b) {
        const nameA = a.name.toLowerCase();
        const nameB = b.name.toLowerCase();
        if (nameA < nameB) {
          return -1;
        } else if (nameA > nameB) {
          return 1;
        } else {
          return 0;
        }
      });
      this.loadingEnterprises = false;
    },
    /**
     * Carga los grupos en el selector
     * @param {*} enterpriseId
     */
    async getGroups(enterpriseId, groupId) {
      this.loadingGroups = true;
      const groups = await groupService.getGroupsByEnterpriseId(enterpriseId);
      this.groups = groups?.sort(function sort(a, b) {
        const nameA = a.name.toLowerCase();
        const nameB = b.name.toLowerCase();
        if (nameA < nameB) {
          return -1;
        } else if (nameA > nameB) {
          return 1;
        } else {
          return 0;
        }
      });
      if (groupId) {
        this.model.groupId = groupId;
      }
      this.loadingGroups = false;
    },
    /**
     * Carga las flotas en el selector
     */
    async getFleets(enterpriseId, fleetId) {
      this.loadingFleets = true;
      const fleets = await fleetService.getFleetsByEnterpriseId(enterpriseId);
      this.fleets = fleets.sort(function sort(a, b) {
        const nameA = a.name.toLowerCase();
        const nameB = b.name.toLowerCase();
        if (nameA < nameB) {
          return -1;
        } else if (nameA > nameB) {
          return 1;
        } else {
          return 0;
        }
      });
      if (fleetId) {
        this.model.fleetId = fleetId;
      }
      this.loadingFleets = false;
    },
    /**
     * Carga los sectores en el selector
     */
    async getSectors(enterpriseId) {
      this.loadingSectors = true;
      const sectors = await sectorService.getAllByEnterpriseId(enterpriseId);
      this.sectors = sectors?.sort(function sort(a, b) {
        const nameA = a.name.toLowerCase();
        const nameB = b.name.toLowerCase();
        if (nameA < nameB) {
          return -1;
        } else if (nameA > nameB) {
          return 1;
        } else {
          return 0;
        }
      });
      this.loadingSectors = false;
    },
    /**
     * Carga todas las actividades de determinada empresa
     */
    async getActivities(enterpriseId) {
      this.loadingActivities = true;
      const activities = await activityService.getAllByEnterpriseId(
        enterpriseId
      );
      this.activities = activities?.sort(function sort(a, b) {
        const nameA = a.name.toLowerCase();
        const nameB = b.name.toLowerCase();
        if (nameA < nameB) {
          return -1;
        } else if (nameA > nameB) {
          return 1;
        } else {
          return 0;
        }
      });
      this.loadingActivities = false;
    },
    /**
     * Carga los sectores de determinada empresa y de determinado tipo de equipo
     */
    async getSectorsByThingType(enterpriseId, typeId) {
      this.loadingSectors = true;
      const sectors = await sectorService.getAllByEnterpriseIdAndTypeId(
        enterpriseId,
        typeId
      );
      this.sectors = sectors?.sort(function sort(a, b) {
        const nameA = a.name.toLowerCase();
        const nameB = b.name.toLowerCase();
        if (nameA < nameB) {
          return -1;
        } else if (nameA > nameB) {
          return 1;
        } else {
          return 0;
        }
      });
      this.loadingSectors = false;
    },
    async getActivitiesBySectorIdAndTypeId(enterpriseId, sectorId, typeId) {
      this.loadingActivities = true;
      const activities =
        await activityService.getAllByEnterpriseIdSectorIdAndTypeId(
          enterpriseId,
          sectorId,
          typeId
        );
      this.activities = activities?.sort(function sort(a, b) {
        const nameA = a.name.toLowerCase();
        const nameB = b.name.toLowerCase();
        if (nameA < nameB) {
          return -1;
        } else if (nameA > nameB) {
          return 1;
        } else {
          return 0;
        }
      });
      this.loadingActivities = false;
    },
    async getTypes() {
      let types = await typeService.getAllForSelector();
      this.types = types.sort(function sort(a, b) {
        const nameA = a.name.toLowerCase();
        const nameB = b.name.toLowerCase();
        if (nameA < nameB) {
          return -1;
        } else if (nameA > nameB) {
          return 1;
        } else {
          return 0;
        }
      });
    },
    /**
     * Carga el dialogo para crear un grupo
     */
    openGroup() {
      const bread = {
        key: DialogGroup,
        disabled: true,
        text: this.$t("group.name"),
        model: {
          enterprises: clone([
            this.enterprises.find(
              (enterprise) => enterprise._id == this.model.enterpriseId
            ),
          ]),
          disableEnterpriseSelector: true,
          enterpriseId: this.model.enterpriseId,
        },
      };
      this.pushBread(bread);
    },
    /**
     * Carga el dialogo para crear una flota
     */
    openFleet() {
      const bread = {
        key: DialogFleet,
        disabled: true,
        text: this.$t("fleet.name"),
        model: {
          enterprises: clone([
            this.enterprises.find(
              (enterprise) => enterprise._id == this.model.enterpriseId
            ),
          ]),
          disableEnterpriseSelector: true,
          enterpriseId: this.model.enterpriseId,
        },
      };
      this.pushBread(bread);
    },
    /**
     * Setear una empresa
     */
    setEnterprise(enterpriseId, isInitialSetup = false) {
      this.model.enterpriseId = enterpriseId;
      this.sectors = [];
      this.activities = [];
      this.groups = [];
      this.fleets = [];
      if (!isInitialSetup) {
        this.model.sectorId = null;
        this.model.activityId = null;
        this.model.groupId = null;
        this.model.fleetId = null;
      }
      if (enterpriseId) {
        if (!isInitialSetup) {
          this.setCountryFromEnterprise(enterpriseId);
        }
        this.getGroups(enterpriseId);
        this.getFleets(enterpriseId);
        if (this.model.typeId) {
          this.getSectorsByThingType(enterpriseId, this.model.typeId);
        }
      }
      this.$emit("enterpriseIdSelected", enterpriseId);
      this.$emit("updateConfigNeeded");
    },
    /**
     * Setear un tipo de equipo
     */
    setThingType(typeId, isInitialSetup = false) {
      this.model.typeId = typeId;
      const typeSelected = this.types?.find((type) => type._id === typeId);
      if(typeSelected?.key !== "HARVESTER") {
        this.model.ignoreStoppedForLackEmptyBox = false;
      }
      if (typeSelected?.key != null) {
        if (!isInitialSetup) {
          this.model.sectorId = null;
        }
        this.getSectorsByThingType(this.model.enterpriseId, typeId);
        this.$emit("typeKeySelected", typeSelected.key);
      }
      /**
       *  Emite un evento para que se actualice la configuración de RUS y Eventos
       */
      this.$emit("updateConfigNeeded");
    },
    /**
     * Setear un sector
     */
    setSector(sectorId, isInitialSetup = false) {
      this.model.sectorId = sectorId;
      if (!isInitialSetup) {
        this.model.activityId = null;
      }
      if (sectorId) {
        this.getActivitiesBySectorIdAndTypeId(
          this.model.enterpriseId,
          sectorId,
          this.model.typeId
        );
      }
    },
    /**
     * Limpiar el selector de tipo de equipo
     */
    clearThingType() {
      this.setThingType(null);
    },
    /**
     * Cada vez que se modifica la selección de la empresa, se cargan
     */
    enterpriseSelectedFunction(newValue) {
      this.model.enterpriseId =
        newValue && newValue._id ? newValue._id : newValue;
      this.setEnterprise(this.model.enterpriseId);
    },
    clearEnterprise(val) {
      if (val) {
        this.setEnterprise(null);
      }
    },
    /**
     * Cada vez que se modifica la selección del país
     */
    countrySelectedFunction(newValue) {
      this.model.countryId = newValue && newValue._id ? newValue._id : newValue;
      this.countryManuallySelected = true;
    },
    clearCountry(val) {
      if (val) {
        this.model.countryId = null;
        this.countryManuallySelected = true;
      }
    },
    /**
     * Cada vez que se modifica la selección del tipo, se cargan
     */
    typeSelectedFunction(newValue) {
      this.model.typeId = newValue && newValue._id ? newValue._id : newValue;
      this.setThingType(this.model.typeId);
    },
    clearType(val) {
      if (val) {
        this.model.typeId = null;
        this.model.sectorId = null;
        this.model.activityId = null;
      }
    },
    /**
     * Cada vez que se modifica la selección del sector, se cargan
     */
    sectorSelectedFunction(newValue) {
      this.model.sectorId = newValue && newValue._id ? newValue._id : newValue;
      this.setSector(this.model.sectorId);
    },
    clearSector(val) {
      if (val) {
        this.model.sectorId = null;
        this.model.activityId = null;
      }
    },
    /**
     * Cada vez que se modifica la selección de la actividad, se cargan
     */
    activitySelectedFunction(newValue) {
      this.model.activityId =
        newValue && newValue._id ? newValue._id : newValue;
    },
    clearActivity(val) {
      if (val) {
        this.model.activityId = null;
      }
    },
    /**
     * Cada vez que se modifica la selección del grupo, se cargan
     */
    groupSelectedFunction(newValue) {
      this.model.groupId = newValue && newValue._id ? newValue._id : newValue;
    },
    clearGroup(val) {
      if (val) {
        this.model.groupId = null;
      }
    },
    /**
     * Cada vez que se modifica la selección de la flota, se cargan
     */
    fleetSelectedFunction(newValue) {
      this.model.fleetId = newValue && newValue._id ? newValue._id : newValue;
    },
    clearFleet(val) {
      if (val) {
        this.model.fleetId = null;
      }
    },
    checkHarvester() {
      const typeSelected = this.types?.find((type) => type._id === this.model.typeId);
      if(typeSelected?.key === "HARVESTER") {
        return false;
      }
      return true;
    }
  },
  watch: {
    /**
     * Para restablecer las validaciones del formulario
     * @param {*} newVal
     */
    resetFormValidation(newVal) {
      if (newVal && this.$refs.thingTypeTabForm) {
        this.$refs.thingTypeTabForm.resetValidation();
      }
    },
    isValidForm(newVal, oldVal) {
      if (newVal !== oldVal) {
        this.$emit("update:validForm", newVal);
      }
    },
  },
};
